import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Rocket, 
  CheckCircle, 
  AlertTriangle, 
  Info,
  ExternalLink,
  Shield,
  Database,
  Key,
  Zap
} from 'lucide-react';
import PreLaunchVerification from '@/components/admin/PreLaunchVerification';

const PreLaunchChecklist: React.FC = () => {
  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Rocket className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-3xl font-bold">Pre-Launch Checklist</h1>
          <p className="text-muted-foreground">Verify all systems before going live</p>
        </div>
      </div>

      {/* Critical Status Alert */}
      <Alert>
        <CheckCircle className="h-4 w-4" />
        <AlertTitle>✅ Course Data Fixed</AlertTitle>
        <AlertDescription>
          All 185 courses now have valid level values. 38 courses were automatically normalized from invalid levels (basic → beginner, expert → advanced).
        </AlertDescription>
      </Alert>

      {/* Automated Verification Tests */}
      <PreLaunchVerification />

      {/* Manual Configuration Required */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Manual Security Configuration
          </CardTitle>
          <CardDescription>
            These settings require manual configuration in Supabase Dashboard
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Leaked Password Protection */}
          <div className="space-y-3 p-4 border rounded-lg">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Key className="h-4 w-4 text-yellow-600" />
                  <h4 className="font-medium">Enable Leaked Password Protection</h4>
                  <Badge variant="outline" className="ml-2">5 minutes</Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  Prevents users from using passwords found in known data breaches
                </p>
              </div>
              <Badge variant="outline" className="border-yellow-500 text-yellow-700">
                Required
              </Badge>
            </div>

            <div className="space-y-2 text-sm">
              <p className="font-medium">Steps:</p>
              <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                <li>Go to Supabase Dashboard → Authentication → Settings</li>
                <li>Find "Password Policy" section</li>
                <li>Toggle on "Leaked Password Protection"</li>
                <li>Save changes</li>
              </ol>
            </div>

            <Button 
              variant="outline" 
              size="sm"
              className="w-full"
              onClick={() => window.open('https://supabase.com/dashboard/project/wxgazidevakcnkrngykk/auth/policies', '_blank')}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Open in Supabase Dashboard
            </Button>
          </div>

          {/* PostgreSQL Upgrade */}
          <div className="space-y-3 p-4 border rounded-lg">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Database className="h-4 w-4 text-blue-600" />
                  <h4 className="font-medium">Schedule PostgreSQL Upgrade</h4>
                  <Badge variant="outline" className="ml-2">Maintenance Window</Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  Apply security patches and performance improvements
                </p>
              </div>
              <Badge variant="outline" className="border-blue-500 text-blue-700">
                Recommended
              </Badge>
            </div>

            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Schedule during low-traffic period. May cause brief downtime.
              </AlertDescription>
            </Alert>

            <Button 
              variant="outline" 
              size="sm"
              className="w-full"
              onClick={() => window.open('https://supabase.com/dashboard/project/wxgazidevakcnkrngykk/settings/infrastructure', '_blank')}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Open Database Settings
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* System Status Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            System Status Summary
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <p className="text-sm font-medium">Courses</p>
              <p className="text-2xl font-bold">185</p>
              <p className="text-xs text-muted-foreground">All levels normalized</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium">Modules</p>
              <p className="text-2xl font-bold">725</p>
              <p className="text-xs text-muted-foreground">100% content coverage</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium">Categories</p>
              <p className="text-2xl font-bold">31</p>
              <p className="text-xs text-muted-foreground">Diverse catalog</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium">Active Users</p>
              <p className="text-2xl font-bold">10</p>
              <p className="text-xs text-muted-foreground">All approved</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Launch Readiness */}
      <Alert>
        <CheckCircle className="h-4 w-4" />
        <AlertTitle>🚀 Almost Ready for Launch</AlertTitle>
        <AlertDescription>
          Complete the manual security configuration above and run all verification tests to ensure production readiness.
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default PreLaunchChecklist;
